// The auction (AUCTION.md, scenario C8, BACKLOG D13): every team the same
// budget, players put up in turn, and whoever bids the most gets him.
//
// THE SAME SEAM AS A PICK DRAFT. `apply`, `advance` and `nextAt` hand a state
// with an `auction` to this file, so the room, the Durable Object, the
// browser's mock and the unattended runner host an auction unchanged. A sale is
// a `DraftPick` with a price; its `round` is the team's nth player and its
// `overall` that square of the board, `(round − 1) × teams + seat`, so an
// auction's board reads like a linear draft's: each team's column, in the
// order it bought.
//
// THE MONEY IS NEVER STORED, ONLY WORKED OUT. What a team has spent is the sum
// of its prices; what it has promised is the players it is ahead on. Nothing
// can drift from the picks, and an undone sale gives the money back by itself.

import type {
  Applied,
  DraftContext,
  DraftEvent,
  DraftIntent,
  DraftPick,
  DraftState,
} from './engine';
import { AI_THINK_MAX_MS, AUTOPILOT_DELAY_MS, carriedBy, isTaken, rosterOf } from './engine';
import { draftStyleOf, styleLook, stylePull, styleRefuses } from './style';

/** The league's auction rules (`LeagueSettings.draft.auction`, AUCTION.md §9). */
export interface AuctionRules {
  budget: number;
  minBid: number;
  nominationSeconds: number;
  bidSeconds: number;
  /** A bid with less than this left puts the clock back to it. */
  resetSeconds: number;
  /** Players up at once: one in a live auction, several in a slow one (AUCTION.md §5). */
  lotsAtOnce: number;
}

/** How a bid was made, which the sale keeps as the pick's `by`. */
type BidBy = DraftPick['by'];

/** The most a team will pay for a player up: an auto-bid, or the bid it made. */
export interface AuctionMax {
  amount: number;
  /** When it was set: an earlier one wins a tie. */
  at: number;
  by: BidBy;
  madeBy?: string;
  /** Set as an auto-bid, which the room bids up to; absent for a team's own last bid. */
  auto?: true;
}

export interface AuctionBid {
  teamId: string;
  amount: number;
  at: number;
  /** Made by the room for an auto-bid, not typed. */
  auto: boolean;
}

/** A player up for bids. */
export interface AuctionLot {
  id: number;
  playerId: string;
  nominatedBy: string;
  openedAt: number;
  /** The high bid, and who has it. */
  price: number;
  leader: string;
  /** When the price last moved: a bot answers a few seconds after it. */
  changedAt: number;
  /** When it sells; null while the room is paused. */
  deadlineAt: number | null;
  /** Running milliseconds left, kept while paused. */
  pausedRemaining: number | null;
  /** Each bidding team's most: an auto-bid, or its own last bid. Private to each team. */
  maxes: Record<string, AuctionMax>;
  bids: AuctionBid[];
}

export interface AuctionState {
  rules: AuctionRules;
  lots: AuctionLot[];
  /** The last lot's number, so a new one never takes an old one's. */
  lotSeq: number;
  /** Whose nomination it is, or null when enough players are up or nobody has room. */
  nominator: string | null;
  nominateBy: number | null;
  /** Running milliseconds left on the nomination, kept while paused. */
  nominateRemaining: number | null;
  /** When the nomination began: a bot and a seat on auto time theirs from it. */
  nominatorSince: number | null;
  /** The seat in `order` the next nomination starts looking from. */
  nextSeat: number;
}

/** An auction's state before it starts. */
export function auctionState(rules: AuctionRules): AuctionState {
  return {
    rules: { ...rules },
    lots: [],
    lotSeq: 0,
    nominator: null,
    nominateBy: null,
    nominateRemaining: null,
    nominatorSince: null,
    nextSeat: 0,
  };
}

// ── Values ────────────────────────────────────────────────────────────────

/**
 * What each player is worth in this league's money (AUCTION.md §7): the money
 * above the lowest bid for every spot, shared among the players worth more
 * than a replacement in proportion to how much more, and the lowest bid for
 * everybody else. `value` is value over replacement, the board's own basis.
 */
export function auctionValues(
  players: readonly { id: string; value: number }[],
  input: { teams: number; rounds: number; budget: number; minBid: number },
): Record<string, number> {
  const spots = input.teams * input.rounds;
  const spare = Math.max(0, input.teams * (input.budget - input.rounds * input.minBid));
  // THE PLAYERS WHO WILL BE BOUGHT, best first: only they share the money.
  const bought = [...players]
    .filter((p) => Number.isFinite(p.value))
    .sort((a, b) => b.value - a.value)
    .slice(0, spots);
  const above = bought.filter((p) => p.value > 0);
  const total = above.reduce((sum, p) => sum + p.value, 0);
  const out: Record<string, number> = {};
  for (const p of players) out[p.id] = input.minBid;
  if (total <= 0) return out;
  // NOBODY WORTH MOST OF A BUDGET: a shallow league's board puts its best
  // player far above the rest, and no manager spends forty percent of his
  // money on one player twice.
  const ceiling = Math.max(input.minBid, Math.round(input.budget * 0.4));
  for (const p of above) {
    out[p.id] = Math.min(
      ceiling,
      input.minBid + Math.max(0, Math.round((spare * p.value) / total)),
    );
  }
  return out;
}

// ── Money ─────────────────────────────────────────────────────────────────

function auctionOf(state: DraftState): AuctionState {
  return state.auction!;
}

/** What a team has paid for the players it has bought. */
export function spentBy(state: DraftState, teamId: string): number {
  return state.picks.reduce((sum, p) => sum + (p.teamId === teamId ? (p.price ?? 0) : 0), 0);
}

/** The players up that a team is ahead on, but for one. */
function leadsOf(state: DraftState, teamId: string, exceptLot: number | null): AuctionLot[] {
  return auctionOf(state).lots.filter((l) => l.leader === teamId && l.id !== exceptLot);
}

/** Spots a team has left, counting the players it is ahead on as bought (AUCTION.md §3.2). */
export function spotsLeft(
  state: DraftState,
  teamId: string,
  exceptLot: number | null = null,
): number {
  return state.rounds - rosterOf(state, teamId).length - leadsOf(state, teamId, exceptLot).length;
}

/** Money a team has left, its leads held back. */
export function moneyLeft(
  state: DraftState,
  teamId: string,
  exceptLot: number | null = null,
): number {
  const rules = auctionOf(state).rules;
  const promised = leadsOf(state, teamId, exceptLot).reduce((sum, l) => sum + l.price, 0);
  return rules.budget - spentBy(state, teamId) - promised;
}

/**
 * The most a team can bid on a lot, or on a new nomination (`lotId` null):
 * its money less the lowest bid for every empty spot after this one. Zero
 * when it has no spot to put him in.
 */
export function mostFor(state: DraftState, teamId: string, lotId: number | null): number {
  const spots = spotsLeft(state, teamId, lotId);
  if (spots <= 0) return 0;
  const rules = auctionOf(state).rules;
  return Math.max(0, moneyLeft(state, teamId, lotId) - rules.minBid * (spots - 1));
}

/** Whether a player is up for bids now. */
export function onBlock(state: DraftState, playerId: string): boolean {
  return auctionOf(state).lots.some((l) => l.playerId === playerId);
}

function gone(state: DraftState, playerId: string): boolean {
  return isTaken(state, playerId) || onBlock(state, playerId);
}

// ── Time ──────────────────────────────────────────────────────────────────

function endsAt(ctx: DraftContext, from: number, ms: number): number {
  return ctx.clockEnds ? ctx.clockEnds(from, ms) : from + ms;
}

function leftOf(ctx: DraftContext, now: number, deadline: number): number {
  return Math.max(0, ctx.clockLeft ? ctx.clockLeft(now, deadline) : deadline - now);
}

function reject(
  state: DraftState,
  code: Extract<DraftEvent, { type: 'rejected' }>['code'],
  message: string,
): Applied {
  return { state, events: [{ type: 'rejected', code, message }] };
}

const money = (n: number) => `$${String(n)}`;

// ── Apply ─────────────────────────────────────────────────────────────────

/** One intent, for a draft that is an auction. `apply` hands it here. */
export function applyAuction(
  state: DraftState,
  intent: DraftIntent,
  now: number,
  ctx: DraftContext,
): Applied {
  switch (intent.type) {
    case 'start': {
      if (state.status !== 'lobby')
        return reject(state, 'already-started', 'The draft has already started.');
      // KEEPERS ARE THE TEAMS' AT THEIR PRICES before the first nomination
      // (PLAN-2026-10-27-the-next-season §2.3): bought by the rules, so the
      // budget and the spots left read them from the first moment.
      let started: DraftState = { ...state, status: 'live', startedAt: now };
      const kept: DraftEvent[] = [];
      for (const k of state.keepers ?? []) {
        const round = rosterOf({ ...started, keepers: [] }, k.teamId).length + 1;
        const seat = started.order.indexOf(k.teamId) + 1;
        if (seat === 0) continue;
        const pick: DraftPick = {
          overall: (round - 1) * started.order.length + seat,
          round,
          slot: seat,
          teamId: k.teamId,
          playerId: k.playerId,
          at: now,
          by: 'keeper',
          price: k.price ?? 0,
        };
        started = { ...started, picks: [...started.picks, pick] };
        kept.push({ type: 'pick.made', pick });
      }
      const opened = openNomination(started, now, ctx);
      return {
        state: opened.state,
        events: [{ type: 'started', at: now }, ...kept, ...opened.events],
      };
    }
    case 'nominate': {
      if (state.status !== 'live') return reject(state, 'not-live', 'The draft is not live.');
      const a = auctionOf(state);
      if (a.nominator !== intent.teamId)
        return reject(state, 'not-your-turn', 'It is not your nomination.');
      return nominate(state, intent.teamId, intent.playerId, intent.bid, now, ctx, {
        by: 'user',
        ...(intent.madeBy === undefined ? {} : { madeBy: intent.madeBy }),
      });
    }
    case 'bid': {
      if (state.status !== 'live') return reject(state, 'not-live', 'The draft is not live.');
      const lot = auctionOf(state).lots.find((l) => l.id === intent.lotId);
      if (lot === undefined) return reject(state, 'lot-closed', 'He has been sold.');
      if (lot.leader === intent.teamId)
        return reject(state, 'ahead', 'You have the high bid already.');
      const amount = Math.floor(intent.amount);
      if (amount <= lot.price)
        return reject(state, 'too-little', `The bid is ${money(lot.price)}: bid more than that.`);
      const room = refuseRoom(state, intent.teamId, lot.id, amount);
      if (room !== null) return room;
      return bid(state, lot.id, intent.teamId, amount, now, ctx, {
        by: 'user',
        ...(intent.madeBy === undefined ? {} : { madeBy: intent.madeBy }),
      });
    }
    case 'bid.auto': {
      if (state.status !== 'live') return reject(state, 'not-live', 'The draft is not live.');
      const lot = auctionOf(state).lots.find((l) => l.id === intent.lotId);
      if (lot === undefined) return reject(state, 'lot-closed', 'He has been sold.');
      return setAuto(state, lot, intent.teamId, intent.max, now, ctx, intent.madeBy);
    }
    case 'pause': {
      if (state.status !== 'live') return reject(state, 'not-live', 'Nothing to pause.');
      const a = auctionOf(state);
      // EVERY CLOCK STOPS, keeping the running time it had left: a night the
      // league's clock is stopped through is not time anybody had.
      const lots = a.lots.map((l) => ({
        ...l,
        pausedRemaining: l.deadlineAt === null ? l.pausedRemaining : leftOf(ctx, now, l.deadlineAt),
        deadlineAt: null,
      }));
      const nominateRemaining = a.nominateBy === null ? null : leftOf(ctx, now, a.nominateBy);
      return {
        state: {
          ...state,
          status: 'paused',
          auction: { ...a, lots, nominateBy: null, nominateRemaining },
        },
        events: [{ type: 'paused', by: intent.by, at: now }],
      };
    }
    case 'resume': {
      if (state.status !== 'paused') return reject(state, 'not-live', 'Nothing to resume.');
      const a = auctionOf(state);
      // AND START AGAIN FROM THE RESUME: a bot answers a moment after it
      // rather than the instant the room comes back.
      const lots = a.lots.map((l) => ({
        ...l,
        deadlineAt: endsAt(ctx, now, l.pausedRemaining ?? a.rules.resetSeconds * 1000),
        pausedRemaining: null,
        changedAt: now,
      }));
      const nominateBy =
        a.nominator === null
          ? null
          : endsAt(ctx, now, a.nominateRemaining ?? a.rules.nominationSeconds * 1000);
      const next: DraftState = {
        ...state,
        status: 'live',
        auction: {
          ...a,
          lots,
          nominateBy,
          nominateRemaining: null,
          nominatorSince: a.nominator === null ? null : now,
        },
      };
      const events: DraftEvent[] = [{ type: 'resumed', by: intent.by, at: now }];
      if (a.nominator !== null)
        events.push({ type: 'nominator', teamId: a.nominator, deadlineAt: nominateBy });
      return { state: next, events };
    }
    case 'undo':
      return undoSale(state, now, ctx);
    case 'queue.set': {
      // THE NOMINATION QUEUE: who to put up when the clock runs out.
      const pool = new Set(ctx.rankings);
      const playerIds = intent.playerIds.filter(
        (id, i, list) => list.indexOf(id) === i && pool.has(id) && !gone(state, id),
      );
      return {
        state: { ...state, queues: { ...state.queues, [intent.teamId]: playerIds } },
        events: [{ type: 'queue.updated', teamId: intent.teamId, playerIds }],
      };
    }
    case 'autopilot.set': {
      if (!state.order.includes(intent.teamId))
        return reject(state, 'unknown-team', 'That team is not in this draft.');
      if (state.ai[intent.teamId])
        return reject(state, 'unknown-team', 'A bot drafts that seat itself.');
      const autopilot = { ...state.autopilot };
      if (intent.on) autopilot[intent.teamId] = true;
      else delete autopilot[intent.teamId];
      const turned: Applied = {
        state: { ...state, autopilot },
        events: [
          { type: 'autopilot.changed', teamId: intent.teamId, on: intent.on, reason: 'manager' },
        ],
      };
      // ON WHILE NOMINATING IS A NOMINATION NOW, as auto on the clock is a pick.
      if (intent.on && state.status === 'live' && auctionOf(state).nominator === intent.teamId) {
        const put = nominateFor(turned.state, intent.teamId, now, ctx);
        return { state: put.state, events: [...turned.events, ...put.events] };
      }
      // AND IT BIDS FOR ME FROM NOW on the players already up.
      return turned;
    }
    case 'pick':
      return reject(state, 'not-live', 'An auction has no picks: nominate a player and bid.');
    case 'clock.set':
      return reject(state, 'not-live', 'An auction’s clocks are the league’s.');
    case 'trade.offer':
    case 'trade.answer':
    case 'trade.withdraw':
    case 'trade.undo':
      return reject(state, 'bad-trade', 'There are no trades during an auction.');
  }
}

/** Why a team cannot bid this much here, or null: no spot, or more than its most. */
function refuseRoom(
  state: DraftState,
  teamId: string,
  lotId: number | null,
  amount: number,
): Applied | null {
  if (!state.order.includes(teamId))
    return reject(state, 'unknown-team', 'That team is not in this draft.');
  if (spotsLeft(state, teamId, lotId) <= 0)
    return reject(state, 'no-room', 'Your roster is full, counting the players you are ahead on.');
  const most = mostFor(state, teamId, lotId);
  if (amount > most) {
    return reject(
      state,
      'too-much',
      `The most you can bid is ${money(most)}, keeping ${money(auctionOf(state).rules.minBid)} for each spot after this one.`,
    );
  }
  return null;
}

// ── Nominating ────────────────────────────────────────────────────────────

/**
 * The next team up to nominate, when fewer players are up than the league
 * puts up at once: in seat order from where it reached, skipping a team with
 * no room. Or the end, when every roster is full.
 */
function openNomination(state: DraftState, now: number, ctx: DraftContext): Applied {
  const a = auctionOf(state);
  if (state.status !== 'live' || a.nominator !== null) return { state, events: [] };
  if (state.order.every((t) => rosterOf(state, t).length >= state.rounds)) {
    return {
      state: {
        ...state,
        status: 'complete',
        completedAt: now,
        auction: { ...a, nominateBy: null, nominatorSince: null },
      },
      events: [{ type: 'completed', at: now }],
    };
  }
  if (a.lots.length >= a.rules.lotsAtOnce) return { state, events: [] };
  const n = state.order.length;
  for (let k = 0; k < n; k += 1) {
    const index = (a.nextSeat + k) % n;
    const teamId = state.order[index]!;
    if (spotsLeft(state, teamId) <= 0) continue;
    const nominateBy = endsAt(ctx, now, a.rules.nominationSeconds * 1000);
    return {
      state: {
        ...state,
        auction: { ...a, nominator: teamId, nominateBy, nominatorSince: now, nextSeat: index },
      },
      events: [{ type: 'nominator', teamId, deadlineAt: nominateBy }],
    };
  }
  // EVERYBODY WITH ROOM IS AHEAD ON A PLAYER UP: the next sale opens it.
  return { state, events: [] };
}

function nominate(
  state: DraftState,
  teamId: string,
  playerId: string,
  amount: number,
  now: number,
  ctx: DraftContext,
  how: { by: BidBy; madeBy?: string },
): Applied {
  if (!ctx.rankings.includes(playerId)) return reject(state, 'unknown-player', 'Unknown player.');
  if (gone(state, playerId)) return reject(state, 'taken', 'He is sold or up already.');
  const a = auctionOf(state);
  const opening = Math.floor(amount);
  if (opening < a.rules.minBid)
    return reject(state, 'too-little', `The lowest bid is ${money(a.rules.minBid)}.`);
  const room = refuseRoom(state, teamId, null, opening);
  if (room !== null) return room;
  const lot: AuctionLot = {
    id: a.lotSeq + 1,
    playerId,
    nominatedBy: teamId,
    openedAt: now,
    price: opening,
    leader: teamId,
    changedAt: now,
    deadlineAt: endsAt(ctx, now, a.rules.bidSeconds * 1000),
    pausedRemaining: null,
    maxes: { [teamId]: { amount: opening, at: now, ...how } },
    bids: [{ teamId, amount: opening, at: now, auto: false }],
  };
  // HE LEAVES EVERY QUEUE: nobody can put him up twice. Each queue he left is
  // told to its team, as a sale is.
  const queues = Object.fromEntries(
    Object.entries(state.queues).map(([t, ids]) => [t, ids.filter((id) => id !== playerId)]),
  );
  const queueEvents: DraftEvent[] = Object.entries(state.queues)
    .filter(([, ids]) => ids.includes(playerId))
    .map(([t]) => ({ type: 'queue.updated', teamId: t, playerIds: queues[t] ?? [] }));
  const next: DraftState = {
    ...state,
    queues,
    auction: {
      ...a,
      lots: [...a.lots, lot],
      lotSeq: lot.id,
      nominator: null,
      nominateBy: null,
      nominateRemaining: null,
      nominatorSince: null,
      nextSeat: (state.order.indexOf(teamId) + 1) % state.order.length,
    },
  };
  const opened = openNomination(next, now, ctx);
  return {
    state: opened.state,
    events: [
      { type: 'nominated', lotId: lot.id, teamId, playerId, amount: opening },
      ...queueEvents,
      ...opened.events,
    ],
  };
}

/** The players a team may put up: in the pool, not sold, not up. Best value first. */
function nominable(state: DraftState, ctx: DraftContext): string[] {
  const values = ctx.auctionValues ?? {};
  return ctx.rankings
    .filter((id) => !gone(state, id))
    .map((id, i) => ({ id, i, v: values[id] ?? 0 }))
    .sort((x, y) => y.v - x.v || x.i - y.i)
    .map((x) => x.id);
}

/**
 * The room nominates for a team: its queue first, then the highest-valued
 * player left, at the lowest bid (Yahoo, ESPN). A bot puts up the
 * highest-valued player it has a place for.
 */
function nominateFor(state: DraftState, teamId: string, now: number, ctx: DraftContext): Applied {
  const a = auctionOf(state);
  const queued = (state.queues[teamId] ?? []).find(
    (id) => ctx.rankings.includes(id) && !gone(state, id),
  );
  const bot = state.ai[teamId] !== undefined;
  const left = nominable(state, ctx);
  // THE QUEUE FIRST for a person's seat, when he fits; then the
  // highest-valued player this seat would have, which a bot nominates too.
  const fits = (id: string) => wanted(state, teamId, id, ctx).amount > 0;
  const playerId =
    (!bot && queued !== undefined && fits(queued) ? queued : undefined) ??
    (bot ? drainFor(state, teamId, left, ctx) : undefined) ??
    left.find(fits) ??
    left[0];
  if (playerId === undefined) return { state, events: [] };
  const by: BidBy = bot ? 'ai' : playerId === queued ? 'queue' : 'rankings';
  return nominate(state, teamId, playerId, a.rules.minBid, now, ctx, { by });
}

/**
 * A bot that spends the room's money (AI_MANAGERS.md §4.7): half its turns, a
 * player near the top it would pay little for, so the others spend on him.
 * Undefined otherwise, and the bot puts up what it wants.
 */
function drainFor(
  state: DraftState,
  teamId: string,
  left: readonly string[],
  ctx: DraftContext,
): string | undefined {
  const style = draftStyleOf(state.ai[teamId]!, ctx, state.id);
  if (style === null || !style.drain) return undefined;
  const a = auctionOf(state);
  if (ctx.random(`${state.id}:drain:${String(a.lotSeq)}`) >= 0.5) return undefined;
  return left.slice(0, 12).find((id) => {
    const value = ctx.auctionValues?.[id] ?? a.rules.minBid;
    // ONE IT COULD STILL OWN: it opens the bidding, and if nobody answers he
    // is its. A player it wants not at all — a full position, a spot its
    // missing starters need, a grudge — is never put up, or the floor breaks.
    const want = wanted(state, teamId, id, ctx).amount;
    return value > a.rules.minBid * 5 && want > 0 && want < value * 0.85;
  });
}

// ── Bidding ───────────────────────────────────────────────────────────────

/**
 * A bid, and then the auto-bids answering it (AUCTION.md §3.4): the team with
 * the highest most is ahead, at $1 over the next, and a tie goes to whoever
 * set theirs first. Every most is capped by what its team can bid now, which
 * moves as the team wins and loses other players up.
 */
function bid(
  state: DraftState,
  lotId: number,
  teamId: string,
  amount: number,
  now: number,
  ctx: DraftContext,
  how: { by: BidBy; madeBy?: string },
): Applied {
  const a = auctionOf(state);
  const lot = a.lots.find((l) => l.id === lotId)!;
  const had = lot.maxes[teamId];
  const placed: AuctionLot = {
    ...lot,
    price: amount,
    leader: teamId,
    maxes: {
      ...lot.maxes,
      // A BID IS ITS OWN MOST unless the team set a higher auto-bid already.
      [teamId]: had !== undefined && had.amount >= amount ? had : { amount, at: now, ...how },
    },
    bids: [...lot.bids, { teamId, amount, at: now, auto: false }],
  };
  const events: DraftEvent[] = [{ type: 'bid', lotId, teamId, amount, auto: false }];
  return settle(
    { ...state, auction: { ...a, lots: replaceLot(a.lots, placed) } },
    lotId,
    now,
    ctx,
    events,
  );
}

/** An auto-bid set, raised, or taken off; a team behind bids with it at once. */
function setAuto(
  state: DraftState,
  lot: AuctionLot,
  teamId: string,
  max: number | null,
  now: number,
  ctx: DraftContext,
  madeBy: string | undefined,
): Applied {
  const a = auctionOf(state);
  const leading = lot.leader === teamId;
  const maxes = { ...lot.maxes };
  if (max === null) {
    // TAKEN OFF: the team ahead keeps its bid, which is its own most.
    if (leading)
      maxes[teamId] = { amount: lot.price, at: lot.maxes[teamId]?.at ?? now, by: 'user' };
    else delete maxes[teamId];
    const next: DraftState = {
      ...state,
      auction: { ...a, lots: replaceLot(a.lots, { ...lot, maxes }) },
    };
    return { state: next, events: [{ type: 'auto.set', teamId, lotId: lot.id, max: null }] };
  }
  const amount = Math.floor(max);
  if (amount <= lot.price && !leading)
    return reject(state, 'too-little', `The bid is ${money(lot.price)}: set more than that.`);
  if (amount < lot.price)
    return reject(state, 'too-little', `You are ahead at ${money(lot.price)} already.`);
  const room = refuseRoom(state, teamId, lot.id, amount);
  if (room !== null) return room;
  const had = lot.maxes[teamId];
  // A RAISE KEEPS ITS PLACE IN A TIE only if it is not a raise.
  maxes[teamId] =
    had !== undefined && had.amount === amount
      ? { ...had, auto: true }
      : { amount, at: now, by: 'user', auto: true, ...(madeBy === undefined ? {} : { madeBy }) };
  const next: DraftState = {
    ...state,
    auction: { ...a, lots: replaceLot(a.lots, { ...lot, maxes }) },
  };
  return settle(next, lot.id, now, ctx, [{ type: 'auto.set', teamId, lotId: lot.id, max: amount }]);
}

function replaceLot(lots: readonly AuctionLot[], lot: AuctionLot): AuctionLot[] {
  return lots.map((l) => (l.id === lot.id ? lot : l));
}

/**
 * One lot's auto-bids answered, and its clock topped up if its price moved
 * late (`moved` says a bid was just placed on it).
 *
 * EVERY TEAM'S MOST IS IN `maxes`: an auto-bid, or the bid it made. Sorted by
 * the most and then by who set it first, the top is ahead, at $1 over the next
 * — or at the next's own amount when the two are level, which the earlier
 * wins (eBay's rule, and a bid "at or under somebody's auto-bid is topped at
 * once"). Each most is capped by what its team can bid now; the team ahead
 * holds at least its own bid, whatever its money says, since nothing it can do
 * lowers a bid it made.
 */
function settleLot(
  state: DraftState,
  lotId: number,
  now: number,
  ctx: DraftContext,
  moved: boolean,
): Applied {
  const a = auctionOf(state);
  let lot = a.lots.find((l) => l.id === lotId)!;
  const events: DraftEvent[] = [];
  const ranked = Object.entries(lot.maxes)
    .map(([teamId, m]) => {
      const eff = Math.min(m.amount, mostFor(state, teamId, lot.id));
      return { teamId, at: m.at, eff: teamId === lot.leader ? Math.max(eff, lot.price) : eff };
    })
    .filter((c) => c.eff >= lot.price)
    .sort((x, y) => y.eff - x.eff || x.at - y.at);
  const top = ranked[0];
  const second = ranked[1];
  if (top !== undefined && second !== undefined) {
    const price = Math.max(
      lot.price,
      top.eff > second.eff ? Math.min(top.eff, second.eff + 1) : top.eff,
    );
    if (price !== lot.price || top.teamId !== lot.leader) {
      lot = {
        ...lot,
        price,
        leader: top.teamId,
        bids: [...lot.bids, { teamId: top.teamId, amount: price, at: now, auto: true }],
      };
      events.push({ type: 'bid', lotId, teamId: top.teamId, amount: price, auto: true });
    }
  }
  if (moved || events.length > 0) {
    // A LATE BID PUTS THE CLOCK BACK: under ten seconds left, it is ten
    // again (all of them), so everybody can answer it.
    const resetMs = a.rules.resetSeconds * 1000;
    const deadlineAt =
      lot.deadlineAt !== null && leftOf(ctx, now, lot.deadlineAt) < resetMs
        ? endsAt(ctx, now, resetMs)
        : lot.deadlineAt;
    lot = { ...lot, deadlineAt, changedAt: now };
  }
  return { state: { ...state, auction: { ...a, lots: replaceLot(a.lots, lot) } }, events };
}

/**
 * Every player up but one answered again, until nothing moves: a team outbid
 * on one player can spend that money on another, and an auto-bid its money
 * held down a moment ago comes back into play (the review of AUCTION.md §5).
 * A team ahead never loses ground to this, so it settles in a few passes.
 */
function settleOthers(
  state: DraftState,
  except: number | null,
  now: number,
  ctx: DraftContext,
): Applied {
  let cur = state;
  const events: DraftEvent[] = [];
  for (let pass = 0; pass < 10; pass += 1) {
    let changed = false;
    for (const lot of auctionOf(cur).lots) {
      if (lot.id === except || lot.deadlineAt === null) continue;
      const r = settleLot(cur, lot.id, now, ctx, false);
      if (r.events.length === 0) continue;
      cur = r.state;
      events.push(...r.events);
      changed = true;
    }
    if (!changed) break;
  }
  return { state: cur, events };
}

/**
 * A bid, or an auto-bid set: that player's auto-bids answered, then every
 * other player up's (`settleOthers`), and the nomination moved on if a team
 * up to nominate has filled its roster with the players it is ahead on.
 */
function settle(
  state: DraftState,
  lotId: number,
  now: number,
  ctx: DraftContext,
  events: DraftEvent[],
): Applied {
  const first = settleLot(
    state,
    lotId,
    now,
    ctx,
    events.some((e) => e.type === 'bid'),
  );
  const others = settleOthers(first.state, lotId, now, ctx);
  const moved = renominate(others.state, now, ctx);
  return {
    state: moved.state,
    events: [...events, ...first.events, ...others.events, ...moved.events],
  };
}

/**
 * A nominator whose roster the players it is ahead on have filled cannot put
 * anybody up: the next team with room nominates instead. And a team that has
 * room again after being outbid can be the one, when nobody was.
 */
function renominate(state: DraftState, now: number, ctx: DraftContext): Applied {
  const a = auctionOf(state);
  if (a.nominator !== null && spotsLeft(state, a.nominator) <= 0) {
    const skipped: DraftState = {
      ...state,
      auction: {
        ...a,
        nominator: null,
        nominateBy: null,
        nominatorSince: null,
        nextSeat: (state.order.indexOf(a.nominator) + 1) % state.order.length,
      },
    };
    const opened = openNomination(skipped, now, ctx);
    return opened.events.length > 0
      ? opened
      : { state: opened.state, events: [{ type: 'nominator', teamId: null, deadlineAt: null }] };
  }
  return openNomination(state, now, ctx);
}

// ── Selling ───────────────────────────────────────────────────────────────

function sell(state: DraftState, lotId: number, at: number, ctx: DraftContext): Applied {
  const a = auctionOf(state);
  const lot = a.lots.find((l) => l.id === lotId)!;
  const round = rosterOf(state, lot.leader).length + 1;
  const seat = state.order.indexOf(lot.leader) + 1;
  const how = lot.maxes[lot.leader];
  const pick: DraftPick = {
    overall: (round - 1) * state.order.length + seat,
    round,
    slot: seat,
    teamId: lot.leader,
    playerId: lot.playerId,
    at,
    by: how?.by ?? 'user',
    ...(how?.madeBy === undefined ? {} : { madeBy: how.madeBy }),
    price: lot.price,
  };
  const next: DraftState = {
    ...state,
    picks: [...state.picks, pick],
    auction: { ...a, lots: a.lots.filter((l) => l.id !== lotId) },
  };
  const opened = openNomination(next, at, ctx);
  return { state: opened.state, events: [{ type: 'sold', lotId, pick }, ...opened.events] };
}

/** The commissioner takes back the last sale: the player is back, and so is the money. */
function undoSale(state: DraftState, now: number, ctx: DraftContext): Applied {
  const last = state.picks[state.picks.length - 1];
  // A KEEPER WAS NOT SOLD: the rules put him on his team before the auction.
  if (last === undefined || last.by === 'keeper')
    return reject(state, 'nothing-to-undo', 'Nothing has been sold yet.');
  const a = auctionOf(state);
  const wasDone = state.status === 'complete';
  const next: DraftState = {
    ...state,
    picks: state.picks.slice(0, -1),
    status: wasDone ? 'live' : state.status,
    completedAt: null,
  };
  const events: DraftEvent[] = [{ type: 'undone', overall: last.overall, playerId: last.playerId }];
  if (!wasDone) {
    // THE MONEY IS BACK, so an auto-bid it held down is answered again.
    const again = settleOthers(next, null, now, ctx);
    return { state: again.state, events: [...events, ...again.events] };
  }
  // A FINISHED AUCTION OPENS AGAIN with the team that had him nominating.
  const reopened = openNomination(
    { ...next, auction: { ...a, nextSeat: state.order.indexOf(last.teamId) } },
    now,
    ctx,
  );
  return { state: reopened.state, events: [...events, ...reopened.events] };
}

// ── Bots and seats on auto ────────────────────────────────────────────────

/**
 * The most a bot or a seat on auto would pay for a player (AUCTION.md §6.1,
 * §8), and where that came from. A seat on auto pays up to its manager's own
 * price, else the value, never above. A bot starts from the value with its own
 * lean, more for a position it still has to start and less for one it has
 * filled, paced by its money left per spot. Nothing for a position with no
 * room left.
 */
function wanted(
  state: DraftState,
  teamId: string,
  playerId: string,
  ctx: DraftContext,
): { amount: number; by: BidBy } {
  const a = auctionOf(state);
  const { minBid } = a.rules;
  const bot = state.ai[teamId];
  const plan = ctx.plans?.[teamId];
  const none = { amount: 0, by: 'rankings' as BidBy };
  if (!bot && plan?.doNotDraft.includes(playerId)) return none;
  const pos = ctx.positionOf(playerId);
  const have = [...rosterOf(state, teamId), ...leadsOf(state, teamId, null).map((l) => l.playerId)];
  const atPos = have.filter((id) => ctx.positionOf(id) === pos).length;
  const cap = Math.min(
    plan?.limits[pos]?.max ?? Infinity,
    ctx.leagueCaps?.[pos] ?? Infinity,
    ctx.maxPerPosition[pos] ?? Infinity,
  );
  if (atPos >= cap) return none;
  // THE LAST SPOTS ARE FOR THE STARTERS STILL MISSING, as `bestAvailable`
  // saves a pick draft's last rounds: once the spots left are only enough for
  // them, nothing else is wanted, so no seat ends with a lineup it cannot fill.
  // A DYNASTY'S CARRIED PLAYERS fill starters too (`DraftState.held`).
  if (!fillsMissing(state, teamId, pos, [...carriedBy(state, teamId), ...have], ctx)) return none;
  const value = ctx.auctionValues?.[playerId] ?? minBid;
  if (!bot) {
    const own = plan?.prices?.[playerId];
    return own === undefined ? { amount: value, by: 'rankings' } : { amount: own, by: 'plan' };
  }
  // ITS STYLE (AI_MANAGERS.md §4.7): a grudge or a limit is no bid at all, and
  // what its plan and tastes make of a player moves what it would pay.
  const style = draftStyleOf(bot, ctx, state.id);
  let taste = 1;
  if (style !== null) {
    const look = styleLook(state, teamId, ctx, style, {
      roster: have,
      auction: true,
      risk: bot.dials.risk,
    });
    if (styleRefuses(style, look, playerId, ctx)) return none;
    taste = Math.min(
      TASTE_MOST,
      Math.max(TASTE_LEAST, 1 + stylePull(style, look, playerId, ctx) / 30),
    );
  }
  const lean = 0.85 + 0.3 * ctx.random(`${state.id}:want:${teamId}:${playerId}`);
  const bold = 0.95 + 0.1 * bot.dials.aggression;
  const starts = ctx.starterNeeds[pos] ?? 0;
  const need = atPos < starts ? 1.1 : 0.7;
  const spots = Math.max(1, spotsLeft(state, teamId));
  const perSpot = (moneyLeft(state, teamId) - spots * minBid) / spots;
  const average = (a.rules.budget - state.rounds * minBid) / state.rounds;
  const pace = Math.min(1.5, Math.max(0.5, Math.sqrt((perSpot + 1) / (average + 1))));
  // NEVER FAR PAST THE VALUE: a bot that wants a player pays up for him, but
  // not a third over what he is worth, or it has nothing left for the rest —
  // a little more for one its tastes are set on.
  const lifted = minBid + (value - minBid) * lean * bold * need * pace * taste;
  const most = minBid + (value - minBid) * 1.25 * Math.max(1, Math.min(taste, TASTE_OVER));
  return {
    amount: Math.max(minBid, Math.round(Math.min(lifted, most))),
    by: 'ai',
  };
}

/** How far a bot's style moves what it would pay: a share of the value either way. */
const TASTE_LEAST = 0.6;
const TASTE_MOST = 1.4;
/** And how far past its usual most a taste lets it go. */
const TASTE_OVER = 1.2;

/**
 * Whether a player at `pos` may take one of this team's spots: always, while
 * it has more spots than starters to find; otherwise only at a position it
 * still has to start.
 */
function fillsMissing(
  state: DraftState,
  teamId: string,
  pos: string,
  have: readonly string[],
  ctx: DraftContext,
): boolean {
  let missing = 0;
  let here = 0;
  for (const [p, count] of Object.entries(ctx.starterNeeds)) {
    const gap = Math.max(0, count - have.filter((id) => ctx.positionOf(id) === p).length);
    missing += gap;
    if (p === pos) here = gap;
  }
  return here > 0 || spotsLeft(state, teamId) > missing;
}

/**
 * Whether the room bids for a person's seat: its manager turned auto on, or
 * is not in the room (AUCTION.md §6.1). A host that says nobody's presence —
 * the unattended runner, a test — puts the seats it means on auto itself.
 */
function biddingForItself(state: DraftState, teamId: string, ctx: DraftContext): boolean {
  if (state.autopilot[teamId]) return true;
  return ctx.isPresent !== undefined && !ctx.isPresent(teamId);
}

/** When a bot answers a moved price: a person's few seconds, never the last half-second. */
function botBidAt(state: DraftState, lot: AuctionLot, teamId: string, ctx: DraftContext): number {
  const bot = state.ai[teamId]!;
  const think =
    800 +
    (1 - bot.dials.activity) * 2_200 +
    ctx.random(`${state.id}:bid:${String(lot.id)}:${teamId}:${String(lot.bids.length)}`) * 1_500;
  const at = lot.changedAt + Math.round(think);
  return lot.deadlineAt === null ? at : Math.max(lot.changedAt, Math.min(at, lot.deadlineAt - 500));
}

/**
 * When a seat on auto puts its price on a player up: a moment after he is up,
 * each seat at its own moment, so two seats with the same price are not
 * settled by where they sit at the table.
 */
function autoBidAt(state: DraftState, lot: AuctionLot, teamId: string, ctx: DraftContext): number {
  const jitter = Math.round(ctx.random(`${state.id}:auto:${String(lot.id)}:${teamId}`) * 400);
  const at =
    lot.openedAt + Math.min(AUTOPILOT_DELAY_MS, state.auction!.rules.bidSeconds * 500) + jitter;
  return lot.deadlineAt === null ? at : Math.min(at, lot.deadlineAt - 1);
}

/** When the team nominating does it by itself: a bot's think, auto's moment, or the clock. */
function nominationDue(
  state: DraftState,
  ctx: DraftContext,
): { at: number; why: 'bot' | 'auto' | 'clock' } | null {
  const a = auctionOf(state);
  const teamId = a.nominator;
  if (teamId === null || a.nominateBy === null || a.nominatorSince === null) return null;
  const span = a.rules.nominationSeconds * 1000;
  const due: { at: number; why: 'bot' | 'auto' | 'clock' }[] = [];
  const bot = state.ai[teamId];
  if (bot) {
    const share =
      0.15 +
      (1 - bot.dials.activity) * 0.55 +
      ctx.random(`${state.id}:nominate:${String(a.lotSeq)}`) * 0.2;
    due.push({
      at: Math.round(a.nominatorSince + share * Math.min(span, AI_THINK_MAX_MS)),
      why: 'bot',
    });
  } else if (state.autopilot[teamId]) {
    due.push({ at: a.nominatorSince + Math.min(AUTOPILOT_DELAY_MS, span), why: 'auto' });
  }
  if (state.whenTheClockExpires !== 'off') due.push({ at: a.nominateBy, why: 'clock' });
  if (due.length === 0) return null;
  return due.reduce((x, y) => (y.at < x.at ? y : x));
}

type Due =
  | { at: number; kind: 'sell'; lotId: number }
  | { at: number; kind: 'bot'; lotId: number; teamId: string }
  | { at: number; kind: 'auto'; lotId: number; teamId: string }
  | { at: number; kind: 'nominate'; why: 'bot' | 'auto' | 'clock' };

/** Everything the room will do by itself, soonest first. */
function dueList(state: DraftState, ctx: DraftContext): Due[] {
  if (state.status !== 'live') return [];
  const a = auctionOf(state);
  const out: Due[] = [];
  for (const lot of a.lots) {
    if (lot.deadlineAt === null) continue;
    out.push({ at: lot.deadlineAt, kind: 'sell', lotId: lot.id });
    for (const teamId of state.order) {
      if (teamId === lot.leader) continue;
      const bot = state.ai[teamId] !== undefined;
      const auto = !bot && biddingForItself(state, teamId, ctx);
      if (!bot && !auto) continue;
      // A SEAT ON AUTO PUTS ITS PRICE IN ONCE, as an auto-bid that answers for
      // it after; a bot comes back every time the price moves. A bid it made
      // by hand, or the opening bid of a player it put up, is not its price.
      if (auto && lot.maxes[teamId]?.auto) continue;
      const most = Math.min(
        wanted(state, teamId, lot.playerId, ctx).amount,
        mostFor(state, teamId, lot.id),
      );
      if (most < lot.price + 1) continue;
      out.push(
        bot
          ? { at: botBidAt(state, lot, teamId, ctx), kind: 'bot', lotId: lot.id, teamId }
          : { at: autoBidAt(state, lot, teamId, ctx), kind: 'auto', lotId: lot.id, teamId },
      );
    }
  }
  const nominating = nominationDue(state, ctx);
  if (nominating !== null) out.push({ at: nominating.at, kind: 'nominate', why: nominating.why });
  // Bids before the sale they would beat; the earlier first.
  const rank = { bot: 0, auto: 0, nominate: 1, sell: 2 } as const;
  return out.sort((x, y) => x.at - y.at || rank[x.kind] - rank[y.kind]);
}

/** When an auction next has something to do by itself, or null. */
export function auctionNextAt(state: DraftState, ctx: DraftContext): number | null {
  return dueList(state, ctx)[0]?.at ?? null;
}

/**
 * A bot's bid: a jump toward what it would pay while it is far below it, and
 * a dollar or two once it is close, as people bid. Big jumps early are what
 * keep a room of bots from raising each other a dollar at a time for a
 * minute: a contested player settles in a handful of bids.
 */
function botAmount(
  state: DraftState,
  lot: AuctionLot,
  teamId: string,
  most: number,
  ctx: DraftContext,
): number {
  const gap = most - lot.price;
  if (gap <= 2) return lot.price + 1;
  const r = ctx.random(`${state.id}:jump:${String(lot.id)}:${teamId}:${String(lot.bids.length)}`);
  const share = gap > 20 ? 0.4 + 0.35 * r : 0.2 + 0.3 * r;
  return Math.min(most, lot.price + Math.max(1, Math.round(gap * share)));
}

/** The auction moved forward to `now`: bots bid, seats on auto bid, nominations made, players sold. */
export function advanceAuction(state: DraftState, now: number, ctx: DraftContext): Applied {
  let cur = state;
  const events: DraftEvent[] = [];
  for (let guard = 0; guard < 100_000; guard += 1) {
    const next = dueList(cur, ctx)[0];
    if (next === undefined || next.at > now) break;
    let step: Applied;
    if (next.kind === 'sell') {
      step = sell(cur, next.lotId, next.at, ctx);
    } else if (next.kind === 'bot' || next.kind === 'auto') {
      const lot = auctionOf(cur).lots.find((l) => l.id === next.lotId)!;
      const want = wanted(cur, next.teamId, lot.playerId, ctx);
      const most = Math.min(want.amount, mostFor(cur, next.teamId, lot.id));
      step =
        next.kind === 'bot'
          ? bid(
              cur,
              lot.id,
              next.teamId,
              botAmount(cur, lot, next.teamId, most, ctx),
              next.at,
              ctx,
              { by: 'ai' },
            )
          : autoFor(cur, lot, next.teamId, want.amount, want.by, next.at, ctx);
    } else {
      const teamId = auctionOf(cur).nominator!;
      step = nominateFor(cur, teamId, next.at, ctx);
      if (step.events.length === 0) break;
      // A SEAT NOBODY IS SITTING IN STAYS ON AUTO after its clock ran out,
      // as in a pick draft (`autopilotOnExpiry`).
      if (
        next.why === 'clock' &&
        step.state.autopilotOnExpiry &&
        !step.state.ai[teamId] &&
        !step.state.autopilot[teamId] &&
        !(ctx.isPresent?.(teamId) ?? false)
      ) {
        step = {
          state: { ...step.state, autopilot: { ...step.state.autopilot, [teamId]: true } },
          events: [
            ...step.events,
            { type: 'autopilot.changed', teamId, on: true, reason: 'clock' },
          ],
        };
      }
    }
    if (step.events.some((e) => e.type === 'rejected')) break;
    cur = step.state;
    events.push(...step.events);
  }
  return { state: cur, events };
}

/**
 * A seat on auto's price, put in as an auto-bid (AUCTION.md §6.1): its own
 * price, which each settle caps at what the team can bid then, so money
 * freed later counts.
 */
function autoFor(
  state: DraftState,
  lot: AuctionLot,
  teamId: string,
  most: number,
  by: BidBy,
  at: number,
  ctx: DraftContext,
): Applied {
  const a = auctionOf(state);
  const maxes = { ...lot.maxes, [teamId]: { amount: most, at, by, auto: true as const } };
  const next: DraftState = {
    ...state,
    auction: { ...a, lots: replaceLot(a.lots, { ...lot, maxes }) },
  };
  const settled = settle(next, lot.id, at, ctx, []);
  // NOTHING MOVED, but the seat has had its say: an event keeps the loop honest.
  return settled.events.length > 0
    ? settled
    : { state: settled.state, events: [{ type: 'auto.set', teamId, lotId: lot.id, max: most }] };
}
